import React, { useState, useRef, useEffect } from "react";
import {
  Container,
  Table,
  Form,
  Button,
  Row,
  Col,
  Card,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  FaFilePdf,
  FaPrint,
  FaPlus,
  FaTrash,
  FaDownload,
} from "react-icons/fa";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { format } from "date-fns";
import html2canvas from "html2canvas";
import { font } from "../../../../fonts/font";

// Function to add custom font
var callAddFont = function () {
  this.addFileToVFS("Amiri-Regular-normal.ttf", font);
  this.addFont("Amiri-Regular-normal.ttf", "Amiri-Regular", "normal");
};

jsPDF.API.events.push(["addFonts", callAddFont]);

const Invoice = () => {
  const { t: _ } = useTranslation("global");
  const [searchParams] = useSearchParams();
  const invoiceRef = useRef(null);

  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isRTL, setIsRTL] = useState(false);

  // Check language from URL params
  useEffect(() => {
    const lang = searchParams.get("lang") || searchParams.get("lng") || "en";
    setIsRTL(lang === "ar" || lang === "arabic");
  }, [searchParams]);

  const [formData, setFormData] = useState({
    invoiceNumber: "#013",
    invoiceDate: "2024/10/01",
    periodFrom: "30/07/2024",
    periodTo: "01/09/2024",
    currency: "AED",
    customerName: "Tagger",
    customerVATNo: "104029128600003",
    commercialRegister: "",
    customerAddress: " Dubai",
    services: [
      {
        id: 1,
        service: "معالجة الطلبات Pack | Pick & Pick",
        quantity: 2,
        unitPrice: 104,
        value: 208,
      },
      {
        id: 2,
        service: "مصاريف الشحن والمتوقعات",
        quantity: 52,
        unitPrice: 778,
        value: 40456,
      },
      {
        id: 3,
        service: "مكالمات متابعة الطلبات",
        quantity: 0,
        unitPrice: 0,
        value: 1000,
      },
      {
        id: 4,
        service: "التخزين",
        quantity: 1,
        unitPrice: 1000,
        value: 1000,
      },
    ],
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleServiceChange = (id, field, value) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.map((service) => {
        if (service.id === id) {
          const updatedService = { ...service, [field]: value };
          // Auto-calculate value if quantity or unitPrice changes
          if (field === "quantity" || field === "unitPrice") {
            updatedService.value =
              updatedService.quantity * updatedService.unitPrice;
          }
          return updatedService;
        }
        return service;
      }),
    }));
  };

  const addService = () => {
    const newService = {
      id: Date.now(),
      service: "",
      quantity: 1,
      unitPrice: 0,
      value: 0,
    };
    setFormData((prev) => ({
      ...prev,
      services: [...prev.services, newService],
    }));
  };

  const removeService = (id) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.filter((service) => service.id !== id),
    }));
  };

  const calculateTotal = () => {
    return formData.services.reduce(
      (total, service) => total + service.value,
      0
    );
  };

  const generatePDF = async () => {
    setIsGeneratingPDF(true);

    try {
      const doc = new jsPDF("p", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      // Set font and colors
      doc.setFont("Amiri-Regular");
      doc.setFontSize(20);
      doc.setTextColor(89, 198, 201);

      // Header
      doc.text("ECOMBO PORTAL L.L.C", pageWidth / 2, 20, { align: "center" });

      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.text("إيكوميو للتعامل الإلكتروني ش.ذ.م.م", pageWidth / 2, 30, {
        align: "center",
      });
      doc.text("رقم السجل التجاري: 2184545", pageWidth / 2, 37, {
        align: "center",
      });

      // Invoice Title
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.text("فاتورة - Invoice", pageWidth / 2, 50, { align: "center" });
      doc.text(formData.invoiceNumber, pageWidth / 2, 58, { align: "center" });

      // Invoice Details Table - Two Column Layout
      const invoiceDetailsY = 75;

      const invoiceDetailsColumns = [
        { title: "القيمة", dataKey: "value" },
        { title: "البيان", dataKey: "label" },
      ];

      const invoiceDetailsRows = [
        { label: "رقم الفاتورة :", value: formData.invoiceNumber },
        { label: "تاريخ الفاتورة", value: formData.invoiceDate },
        {
          label: "من فترة - إلى فترة",
          value: `${formData.periodFrom} - ${formData.periodTo}`,
        },
        { label: "العملة", value: formData.currency },
        { label: "اسم العميل", value: formData.customerName },
        { label: "الرقم الضريبي للعميل", value: formData.customerVATNo },
        { label: "السجل التجاري", value: formData.commercialRegister || "" },
        { label: "عنوان العميل", value: formData.customerAddress },
      ];

      // Add page border
      doc.setDrawColor(128, 128, 128);
      doc.setLineWidth(0.5);
      doc.rect(10, 10, pageWidth - 20, pageHeight - 20);

      doc.autoTable({
        columns: invoiceDetailsColumns,
        body: invoiceDetailsRows,
        startY: invoiceDetailsY,
        margin: { horizontal: 15 },
        theme: "grid",
        headStyles: {
          font: "Amiri-Regular",
          fillColor: [89, 198, 201],
          textColor: [255, 255, 255],
          halign: "right",
          lineWidth: 0.1,
          lineColor: [128, 128, 128],
        },
        bodyStyles: {
          font: "Amiri-Regular",
          fontSize: 9,
          halign: "right",
          lineWidth: 0.1,
          lineColor: [128, 128, 128],
        },
        styles: {
          font: "Amiri-Regular",
          fontSize: 9,
          cellPadding: 2,
          lineWidth: 0.1,
          lineColor: [128, 128, 128],
        },
        columnStyles: {
          value: { halign: "right" },
          label: { halign: "right" },
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
      });

      // Services Table
      const tableY = doc.lastAutoTable.finalY + 10;

      const columns = [
        { title: "القيمة", dataKey: "value" },
        { title: "السعر للوحدة", dataKey: "unitPrice" },
        { title: "العدد", dataKey: "quantity" },
        { title: "الخدمة", dataKey: "service" },
        { title: "#", dataKey: "index" },
      ];

      const rows = formData.services.map((service, index) => ({
        index: index + 1,
        service: service.service,
        quantity: service.quantity,
        unitPrice: `${service.unitPrice} ${formData.currency}`,
        value: `${service.value} ${formData.currency}`,
      }));

      // Add total row
      rows.push({
        index: "",
        service: "",
        quantity: "",
        unitPrice: "إجمالي السعر",
        value: `${calculateTotal()} ${formData.currency}`,
      });

      doc.autoTable({
        columns: columns,
        body: rows,
        startY: tableY,
        margin: { horizontal: 15 },
        theme: "grid",
        headStyles: {
          font: "Amiri-Regular",
          fillColor: [89, 198, 201],
          textColor: [255, 255, 255],
          halign: "right",
          lineWidth: 0.1,
          lineColor: [128, 128, 128],
        },
        bodyStyles: {
          font: "Amiri-Regular",
          halign: "right",
          fontSize: 9,
          lineWidth: 0.1,
          lineColor: [128, 128, 128],
        },
        styles: {
          font: "Amiri-Regular",
          fontSize: 9,
          cellPadding: 3,
          lineWidth: 0.1,
          lineColor: [128, 128, 128],
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        didDrawPage: (data) => {
          // Add page number
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFontSize(8);
          doc.text(`Page ${pageCount}`, pageWidth / 2, pageHeight - 12, {
            align: "center",
          });
        },
      });

      // Footer
      const finalY = doc.lastAutoTable.finalY || tableY + 100;

      if (finalY < pageHeight - 30) {
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        doc.text(
          "مكتب رقم F43 مدينه دبي الصناعيه ش ذ م م مسيح شعيب 2",
          pageWidth / 2,
          finalY + 10,
          { align: "center" }
        );
        doc.text(
          "dubai, United Arab Emirates T +97165679366",
          pageWidth / 2,
          finalY + 16,
          { align: "center" }
        );
      }

      // Save the PDF
      doc.save(`invoice-${formData.invoiceNumber}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handlePrint = async () => {
    if (invoiceRef.current) {
      try {
        const canvas = await html2canvas(invoiceRef.current, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
        });

        const imgData = canvas.toDataURL("image/png");

        // Create a hidden iframe for printing
        const printFrame = document.createElement("iframe");
        printFrame.style.position = "fixed";
        printFrame.style.right = "0";
        printFrame.style.bottom = "0";
        printFrame.style.width = "0";
        printFrame.style.height = "0";
        printFrame.style.border = "0";
        document.body.appendChild(printFrame);

        const frameDoc = printFrame.contentWindow.document;
        frameDoc.open();
        frameDoc.write(`
          <html>
            <head>
              <style>
                @page {
                  margin: 10mm;
                  size: A4;
                }
                body { 
                  margin: 0; 
                  padding: 0; 
                  display: flex; 
                  justify-content: center; 
                  align-items: flex-start; 
                  min-height: 100vh;
                  background: white;
                  font-family: Arial, sans-serif;
                }
                img { 
                  max-width: 100%; 
                  height: auto; 
                  display: block;
                  margin: 0;
                  padding: 0;
                }
                @media print {
                  body { 
                    margin: 0; 
                    padding: 0;
                  }
                  img { 
                    max-width: 100%; 
                    height: auto;
                    margin: 0;
                    padding: 0;
                  }
                }
              </style>
            </head>
            <body>
              <img src="${imgData}" alt="Invoice" />
            </body>
          </html>
        `);
        frameDoc.close();

        // Print immediately
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();

        // Remove the iframe after printing
        setTimeout(() => {
          document.body.removeChild(printFrame);
        }, 1000);
      } catch (error) {
        console.error("Error generating print image:", error);
        // Fallback to regular print
        window.print();
      }
    }
  };

  return (
    <Container
      className="bg-light min-vh-100 p-4"
      style={{ maxWidth: "1000px", fontFamily: "Arial, sans-serif" }}
    >
      {/* Form Section */}
      <Card className="mb-4 shadow-sm border-0 rounded-3 d-print-none">
        <Card.Header
          className="  text-white rounded-top-3 fw-bold"
          style={{ background: "rgba(89, 198, 201, 1)" }}
        >
          <h4 className="mb-0">{_("panel.billGenerator")}</h4>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold text-dark">
                  {_("panel.invoiceNumber")}
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.invoiceNumber}
                  onChange={(e) =>
                    handleInputChange("invoiceNumber", e.target.value)
                  }
                  placeholder={_("panel.enterInvoiceNumber")}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold text-dark">
                  {_("panel.invoiceDate")}
                </Form.Label>
                <Form.Control
                  type="date"
                  value={formData.invoiceDate}
                  onChange={(e) =>
                    handleInputChange("invoiceDate", e.target.value)
                  }
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold text-dark">
                  {_("panel.periodFrom")}
                </Form.Label>
                <Form.Control
                  type="date"
                  value={formData.periodFrom}
                  onChange={(e) =>
                    handleInputChange("periodFrom", e.target.value)
                  }
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold text-dark">
                  {_("panel.periodTo")}
                </Form.Label>
                <Form.Control
                  type="date"
                  value={formData.periodTo}
                  onChange={(e) =>
                    handleInputChange("periodTo", e.target.value)
                  }
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold text-dark">
                  {_("panel.customerName")}
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.customerName}
                  onChange={(e) =>
                    handleInputChange("customerName", e.target.value)
                  }
                  placeholder={_("panel.enterCustomerName")}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold text-dark">
                  {_("panel.customerVATNo")}
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.customerVATNo}
                  onChange={(e) =>
                    handleInputChange("customerVATNo", e.target.value)
                  }
                  placeholder={_("panel.enterVATNo")}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold text-dark">
                  {_("panel.commercialRegister")}
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.commercialRegister}
                  onChange={(e) =>
                    handleInputChange("commercialRegister", e.target.value)
                  }
                  placeholder={_("panel.enterCommercialRegister")}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold text-dark">
                  {_("panel.currency")}
                </Form.Label>
                <Form.Select
                  value={formData.currency}
                  onChange={(e) =>
                    handleInputChange("currency", e.target.value)
                  }
                >
                  <option value="AED">AED</option>
                  <option value="SAR">SAR</option>
                  <option value="EGP">EGP</option>
                  <option value="OMR">OMR</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold text-dark">
              {_("panel.customerAddress")}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.customerAddress}
              onChange={(e) =>
                handleInputChange("customerAddress", e.target.value)
              }
              placeholder={_("panel.enterCustomerAddress")}
            />
          </Form.Group>

          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">{_("panel.services")}</h5>
            <Button className="border-0 " size="sm" onClick={addService}>
              <FaPlus /> {_("panel.addService")}
            </Button>
          </div>

          {formData.services.map((service, index) => (
            <Row
              key={service.id}
              className="mb-3 align-items-end bg-light rounded p-3 border-start border-4"
              style={{ borderColor: "rgba(89, 198, 201, 1)" }}
            >
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-dark">
                    {_("panel.service")}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={service.service}
                    onChange={(e) =>
                      handleServiceChange(service.id, "service", e.target.value)
                    }
                    placeholder={_("panel.enterService")}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-dark">
                    {_("panel.quantity")}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={service.quantity}
                    onChange={(e) =>
                      handleServiceChange(
                        service.id,
                        "quantity",
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-dark">
                    {_("panel.unitPrice")}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={service.unitPrice}
                    onChange={(e) =>
                      handleServiceChange(
                        service.id,
                        "unitPrice",
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-dark">
                    {_("panel.value")}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={service.value}
                    onChange={(e) =>
                      handleServiceChange(
                        service.id,
                        "value",
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={1}>
                <Button
                  className="border-0 "
                  size="sm"
                  onClick={() => removeService(service.id)}
                  disabled={formData.services.length === 1}
                >
                  <FaTrash />
                </Button>
              </Col>
            </Row>
          ))}

          <div className="d-flex justify-content-between align-items-center mt-4 gap-2">
            <div className="bg-success bg-opacity-10 border border-success rounded p-3 text-center">
              <h4 className="text-success fw-bold mb-0">
                {_("panel.total")}: {calculateTotal()} {formData.currency}
              </h4>
            </div>
            <div className="d-flex gap-2 flex-wrap justify-content-end">
              <Button
                onClick={generatePDF}
                className="border-0 "
                disabled={isGeneratingPDF}
              >
                <FaDownload />{" "}
                {isGeneratingPDF ? "Generating..." : "Download PDF"}
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Invoice Preview */}
      <div
        ref={invoiceRef}
        className={`invoice-preview bg-white p-4 rounded-3 shadow-sm ${
          isRTL ? "text-end" : ""
        } d-print-block d-print-p-2 d-print-shadow-none d-print-rounded-0 d-print-m-0 d-print-w-100 d-print-max-w-none`}
        style={{
          maxWidth: "800px",
          margin: "0 auto",
          fontFamily: "Arial, sans-serif",
          lineHeight: 1.4,
          color: "#000",
        }}
      >
        {/* Header */}
        <div className="text-center mb-4 d-print-mb-2">
          <h1
            className="mb-0 fw-bold d-print-fs-5 d-print-mb-1"
            style={{ color: "rgba(89, 198, 201, 1)" }}
          >
            ECOMBO PORTAL L.L.C
          </h1>
          <p className="mb-0 d-print-mb-0 d-print-fs-6">
            إيكوميو للتعامل الإلكتروني ش.ذ.م.م
          </p>
          <p className="mb-0 d-print-mb-0 d-print-fs-6">
            رقم السجل التجاري: 2184545
          </p>
        </div>

        <hr className="my-4 d-print-my-2" />

        {/* Invoice Title */}
        <div className="text-center mb-4 d-print-mb-2">
          <h6 className="mb-0 fw-bold d-print-fs-6 d-print-mb-1">
            فاتورة - Invoice
          </h6>
          <h6 className="mb-0 d-print-fs-6 d-print-mb-0">
            {formData.invoiceNumber}
          </h6>
        </div>

        {/* Invoice Info */}
        <Table bordered className="mb-4 d-print-mb-2 d-print-fs-6">
          <tbody>
            <tr>
              <td className="fw-semibold d-print-p-1">
                {isRTL ? "رقم الفاتورة" : _("panel.invoiceNumber")} :
              </td>
              <td className="d-print-p-1">{formData.invoiceNumber}</td>
            </tr>
            <tr>
              <td className="fw-semibold d-print-p-1">
                {isRTL ? "تاريخ الفاتورة" : _("panel.invoiceDate")}
              </td>
              <td className="d-print-p-1">{formData.invoiceDate}</td>
            </tr>
            <tr>
              <td className="fw-semibold d-print-p-1">
                {isRTL
                  ? "من فترة - إلى فترة"
                  : `${_("panel.periodFrom")} - ${_("panel.periodTo")}`}
              </td>
              <td className="d-print-p-1">
                {formData.periodFrom} - {formData.periodTo}
              </td>
            </tr>
            <tr>
              <td className="fw-semibold d-print-p-1">
                {isRTL ? "العملة" : _("panel.currency")}
              </td>
              <td className="d-print-p-1">{formData.currency}</td>
            </tr>
            <tr>
              <td className="fw-semibold d-print-p-1">
                {isRTL ? "اسم العميل" : _("panel.customerName")}
              </td>
              <td className="d-print-p-1">{formData.customerName}</td>
            </tr>
            <tr>
              <td className="fw-semibold d-print-p-1">
                {isRTL ? "الرقم الضريبي للعميل" : _("panel.customerVATNo")}
              </td>
              <td className="d-print-p-1">{formData.customerVATNo}</td>
            </tr>
            <tr>
              <td className="fw-semibold d-print-p-1">
                {isRTL ? "السجل التجاري" : _("panel.commercialRegister")}
              </td>
              <td className="d-print-p-1">{formData.commercialRegister}</td>
            </tr>
            <tr>
              <td className="fw-semibold d-print-p-1">
                {isRTL ? "عنوان العميل" : _("panel.customerAddress")}
              </td>
              <td className="d-print-p-1" style={{ whiteSpace: "pre-line" }}>
                {formData.customerAddress}
              </td>
            </tr>
          </tbody>
        </Table>

        {/* Items Table */}
        <Table bordered className="mb-4 d-print-mb-2 d-print-fs-6">
          <thead>
            <tr>
              <th className="bg-light fw-bold text-center d-print-p-1">
                {isRTL ? "الخدمة" : _("panel.service")}
              </th>
              <th className="bg-light fw-bold text-center d-print-p-1">
                {isRTL ? "الكمية" : _("panel.quantity")}
              </th>
              <th className="bg-light fw-bold text-center d-print-p-1">
                {isRTL ? "سعر الوحدة" : _("panel.unitPrice")}
              </th>
              <th className="bg-light fw-bold text-center d-print-p-1">
                {isRTL ? "القيمة" : _("panel.value")}
              </th>
            </tr>
          </thead>
          <tbody>
            {formData.services.map((service) => (
              <tr key={service.id}>
                <td className="text-center d-print-p-1">{service.service}</td>
                <td className="text-center d-print-p-1">{service.quantity}</td>
                <td className="text-center d-print-p-1">
                  {service.unitPrice} {formData.currency}
                </td>
                <td className="text-center d-print-p-1">
                  {service.value} {formData.currency}
                </td>
              </tr>
            ))}
            <tr className="bg-light fw-bold">
              <td colSpan="3" className="text-center d-print-p-1">
                {isRTL ? "اجمالي السعر" : _("panel.total")}
              </td>
              <td className="text-center d-print-p-1">
                {calculateTotal()} {formData.currency}
              </td>
            </tr>
          </tbody>
        </Table>

        {/* Footer */}
        <div className="text-center d-print-mb-0">
          <p className="mb-0 d-print-mb-0 d-print-fs-6">
            dubai, United Arab Emirates مدينة دين الصناعية في ذهب جميع شعيب 2
          </p>
          <p className="mb-0 d-print-mb-0 d-print-fs-6">T +97165679366</p>
        </div>
      </div>
    </Container>
  );
};

export default Invoice;
